import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import { ProductionGroup } from '@paragon/contract-library';
import { naturalSortByProperty } from '@paragon/utilities';

import { getProductionGroupName } from './getProductionGroupName';

interface ProductionGroupSelectionProps {
  readonly disabled?: boolean;
  readonly activeProductionGroupGuid: string | null;
  readonly setActiveProductionGroupGuid: (productionGroupGuidOrNull: string | null) => void;
  readonly productionGroups: readonly ProductionGroup[];
  readonly includeProjectDataInNames?: boolean;
}

export function ProductionGroupSelection(props: ProductionGroupSelectionProps) {
  const {
    activeProductionGroupGuid,
    disabled,
    includeProjectDataInNames,
    productionGroups,
    setActiveProductionGroupGuid,
  } = props;

  const productionGroupsSorted = useMemo(
    () =>
      naturalSortByProperty(productionGroups, (productionGroup) =>
        getProductionGroupName(productionGroup, includeProjectDataInNames ?? true),
      ),
    [productionGroups, includeProjectDataInNames],
  );

  return (
    <Autocomplete
      size="small"
      options={productionGroupsSorted}
      getOptionLabel={(productionGroup) => getProductionGroupName(productionGroup, includeProjectDataInNames ?? true)}
      renderOption={(props, option) => (
        <li {...props} key={option.guid}>
          {getProductionGroupName(option, includeProjectDataInNames ?? true)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Select Production Group"
          onFocus={(event) => event.target.select()}
        />
      )}
      value={
        activeProductionGroupGuid
          ? (productionGroups.find((productionGroup) => productionGroup.guid === activeProductionGroupGuid) ?? null)
          : null
      }
      onChange={(event, productionGroup) => void setActiveProductionGroupGuid(productionGroup?.guid ?? null)}
      disabled={disabled}
    />
  );
}
